// Types for the Summary module.
// These mirror the contracts agreed with backend (see plan summary-makeover-v2).

import type { ConditionStatus } from './utils/healthColors';

// Real health states the platform tracks (0..4). Optimal + acceptable are grouped
// as "Saludable" in the KPI and legend of this dashboard, but each card / drawer
// still shows the actual state. `unknown` covers machines with no health data
// (platform's 4 / N/A) — rendered in gray with a "-" value.
export type HealthState = 'optimal' | 'acceptable' | 'attention' | 'danger' | 'unknown';

export interface SummaryKpis {
    total_assets: number;
    healthy_count: number;
    attention_count: number;
    danger_count: number;
    /** Undefined until the backend reports the maintenance bucket; the tile stays hidden meanwhile. */
    maintenance_count?: number;
    // Exactly one of these two is shown depending on whether the current node has
    // group children (use coverage_pct) or machine children (use avg_observability_pct).
    avg_observability_pct?: number;
    coverage_pct?: number;
}

// Canonical monitoring-domain codes: the four buckets the platform tracks each machine across —
// ONL (online vibration), PRC (process measurements), OFF (offline routes), AGE (operation hours).
// Single source of truth for the code set AND its display order, shared by coverage chips
// (statusSelectors), tag badges (TagBadge) and the asset drawer's coverage chips.
// Distinct from the alarm taxonomy (feed code) which is MECH/MULTI/PROC/OFF.
export type DomainCode = 'ONL' | 'PRC' | 'OFF' | 'AGE';
export const DOMAIN_ORDER: readonly DomainCode[] = ['ONL', 'PRC', 'OFF', 'AGE'];

export interface SummaryAsset {
    id: number;
    name: string;
    type: string; // MACHINE subtype shown as icon
    location: string; // breadcrumb-like string
    health_state: HealthState;
    health_score: number | null; // 0-100; null for `unknown` (rendered blank with empty bar)
    observability_pct: number | null; // 0-100; null for `unknown` (same empty rendering)
    // Optional layout coordinates; fallback to treeMap is handled client-side
    x?: number;
    y?: number;
}

export interface SummaryAssetsResponse {
    items: SummaryAsset[];
    has_layout: boolean;
}

export interface LayoutRegion {
    id: string;
    label: string;
    color: string; // hex or token name
    bbox: [number, number, number, number]; // x1, y1, x2, y2
    style: 'solid' | 'dashed';
}

export interface SummaryLayoutResponse {
    regions: LayoutRegion[];
}

// ============================================================================
// Asset detail / history — the detail payload is assembled from live data in the AssetDrawer;
// history types are shared with Feed (feedAPI).
// ============================================================================

// ---- Alarm feature-table (GET /api/alarms/feature_table/) ----
// Base64 typed-array payload, same encoding as the measurements-v3 API.
export interface EncodedArray {
    encoding: string; // JS TypedArray name, e.g. "Float32Array", "Int32Array"
    values: string; // base64
}

export interface FeatureThreshold {
    severity: number;
    y_upper: number | null;
    y_lower: number | null;
    x_band: { lo: number; hi: number } | null;
    triggered: boolean;
    triggered_at: string | null;
}

export interface FeatureTableRow {
    alarm_id: number;
    node_id: number; // sensor node
    domain: 'timeseries' | 'frequency';
    feature: string; // e.g. "timeseries-v_rms"
    thresholds: FeatureThreshold[];
    max_triggered_severity: number | null;
    current: number | null;
    // v3-style encoded series: x = day offset from trigger, y = value (NaN = gap).
    // [] when there's no data in the window (compact); null when values weren't requested.
    series: { x: EncodedArray; y: EncodedArray } | [] | null;
}

export interface HealthTrend {
    points: Array<number | null>; // daily health-score means, oldest -> newest; null = gap day
    start_label: string; // e.g. "Feb 1"
    end_label: string; // e.g. "Hoy"
}

export interface AssetAuthor {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    is_maper_employee?: boolean;
}

export interface AssetDiagnostic {
    date: string | null; // ISO timestamp; formatted in UI via formatDate
    detected_problems: string[]; // pre-formatted, e.g. "Desalineación (Acople, Bomba)"
    best_practices: string[];
    author: AssetAuthor | null;
}

/**
 * A discovered condition variable, already labelled. `caption` is present only for the
 * keys in `CONDITION_COLUMN_VOCABULARY` that define one, which render as a two-level head.
 */
export interface ConditionMatrixColumn {
    id: string;
    title: string;
    caption?: string;
}

export interface ConditionMatrixRow {
    point: string;
    node_id: number;
    /** One cell per column, in column order. */
    cells: Array<{ health: number | null; status: ConditionStatus }>;
}

export interface ConditionMatrix {
    // Machine-specific: the union of the machine's alarm variables and its points' health keys.
    columns: ConditionMatrixColumn[];
    rows: ConditionMatrixRow[];
}

export interface LastIntervention {
    id: string | null;
    date: string | null; // ISO timestamp; formatted in UI via formatDate
    title: string;
    description: string;
    author: AssetAuthor | null;
    created_at?: string | null;
}

/**
 * A measured value with its number and unit apart, so the condition tables can put the
 * unit on a second line and keep the numbers vertically aligned (tabular-nums).
 */
export interface ConditionValue {
    text: string;
    unit?: string;
    raw: number;
}

// ---- Online variable table (GET /api/alarms/variable_table/) ----
// One row per (sensor, variable) of every online measurement point, including variables
// with no alarm and variables with no data, so a full per-point grid can be drawn.

export interface ConditionLimits {
    attention: { y_upper: number | null; y_lower: number | null } | null;
    danger: { y_upper: number | null; y_lower: number | null } | null;
}

export interface VariableTableRow {
    point_node_id: number;
    point_type: string | null;
    monitoring_technique: string | null;
    node_id: number; // sensor node
    feature: string; // "timeseries-v_rms"
    variable: string; // "v_rms"
    axis: 'x' | 'y' | 'z' | null; // null for the temperature sensor
    unit_system: string | null;
    value: number | null;
    date: string | null; // ISO; how stale the value is
    /** The sample before `value`. Minutes earlier, so its trend is a short baseline. */
    previous_value: number | null;
    previous_date: string | null;
    trend: 'up' | 'down' | 'flat' | null;
    measurement_status: string | null;
    /** Stored per-variable health (0..3), the reading the tree and matrix show. */
    health: 0 | 1 | 2 | 3 | null;
    /** Read-time comparison of `value` against the bounds; can disagree with `health`. */
    status: 0 | 2 | 3 | null;
    alarm_id: number | null;
    /** The danger bound, or null when no limit is configured. */
    threshold: number | null;
    threshold_side: 'upper' | 'lower' | null;
    limits: ConditionLimits | null;
}

// ---- Offline variables (GET /api/equipment_nodes/offline_status/?include_variables=true) ----

export interface OfflineReading {
    value: number | null;
    date: string | null;
}

export interface OfflineVariableRow {
    point_node_id: number;
    point_name: string;
    node_id: number | null;
    variable: string; // catalogue key, e.g. "oil_fe" / "t_delta"
    /**
     * The variable's measurement kind (`wear`, `iso_4406`, `temperature`), or the sensor
     * axis for offline vibration. For lab techniques it doubles as the global-metadata
     * feature key that carries the unit and precision, as `timeseries-<group>`.
     */
    group: string | null;
    /** `reference` variables are context (thermography's ambient reading), never judged. */
    role: 'judged' | 'reference';
    previous: OfflineReading;
    current: OfflineReading;
    trend: { direction: 'up' | 'down' | 'flat'; delta: number; pct: number | null } | null;
    status: 0 | 2 | 3 | null;
    alarm_id: number | null;
    threshold: number | null;
    threshold_side: 'upper' | 'lower' | null;
    limits: ConditionLimits | null;
}

// ---- Technique card render model ----

export type TechniqueMode = 'ONL' | 'OFF';

export interface TechniqueVariable {
    key: string; // stable React key
    label: string; // "X · Low frequency" online, "Reductor LA · Iron (Fe)" offline
    caption?: string;
    threshold: ConditionValue | null;
    current: ConditionValue | null;
    previous?: ConditionValue | null; // offline only
    previousDate?: string | null;
    currentDate?: string | null;
    trend?: 'up' | 'down' | 'flat'; // offline only: online samples are minutes apart
    status: ConditionStatus;
    health: number | null;
    /** current / threshold, as a percentage — elects the witness of a healthy point. */
    limitPct: number | null;
    /** Which bound the danger limit sets, so a breach can point the right way. */
    thresholdSide: 'upper' | 'lower' | null;
    /** Sensor node and feature, for the Analysis link. */
    nodeId: number | null;
    feature: string;
}

export interface TechniqueWitness {
    variable: TechniqueVariable;
    /** i18n key of a prefix, e.g. "Closest to limit" when the point is healthy. */
    prefixKey?: string;
    /** Measurement point name, shown when the technique has more than one point. */
    point?: string;
}

export interface MeasurementPointGroup {
    nodeId: number;
    name: string;
    health: number | null;
    status: ConditionStatus;
    variables: TechniqueVariable[];
    witness: TechniqueWitness | null;
}

export type TechniqueProvenance =
    | { kind: 'online'; lastMeasurementAt: string | null }
    | { kind: 'offline'; previousDate: string | null; currentDate: string | null };

export interface TechniqueCard {
    id: string; // 'online_vibration' | 'temperature' | 'offline_*'
    label: string;
    subtitle: string;
    mode: TechniqueMode;
    health: number | null;
    status: ConditionStatus;
    /** Online vibration only: one collapsible panel per measurement point. */
    points: MeasurementPointGroup[];
    /** Flat list, for techniques without a point level. */
    variables: TechniqueVariable[];
    /** The worst variable, or the closest to its limit when healthy. Null when optimal. */
    witness: TechniqueWitness | null;
    /** Ultrasound and electrical analysis: no measurement model exists to report from. */
    headerOnly: boolean;
    provenance: TechniqueProvenance | null;
}

// Variables can render in several shapes inside a domain block.
export type AssetVariable =
    | {
          kind: 'metric';
          name: string;
          feature_label?: string; // monitoring feature (e.g. "Aceleración RMS"); shown muted under the name
          threshold?: string;
          value: string;
          unit?: string;
          trend?: 'up' | 'down';
          in_alarm?: boolean;
          severity?: number; // max triggered severity (2=attention, 3=danger) -> color
          threshold_value?: number | null; // raw bound, for the sparkline threshold line
          triggered_at?: string | null; // ISO; rendered as relative "time ago"
          // Sparkline points [x, value] (x = day offset from trigger); null value = gap.
          series?: Array<[number, number | null]>;
      }
    | {
          kind: 'bands';
          name: string;
          feature_label?: string; // monitoring feature; shown muted under the name
          bands: Array<{ label: string; tone: 'ok' | 'warn' | 'alarm' }>;
          triggered_at?: string | null; // ISO; rendered as relative "time ago"
      }
    | {
          kind: 'status';
          name: string;
          meta?: string;
          status: { label: string; tone: 'pending' | 'stale' | 'ok' };
      }
    | { kind: 'counter'; name: string; seconds: number; live?: boolean; sinceMs?: number }
    | { kind: 'text'; name: string; value: string };

export interface AssetDetail {
    id: number;
    name: string;
    location: string;
    health_score: number | null;
    health_state: HealthState;
    health_text: string; // contextual message: "El activo se encuentra con riesgo de falla."
    diagnostic: AssetDiagnostic | null;
    condition_matrix: ConditionMatrix | null;
    /** One card per monitoring technique; the mode is a chip, not a grouping level. */
    techniques: TechniqueCard[];
    /**
     * Accumulated run-time, shown in its own section (mirrors `OperatingTime`). Null when the
     * technique is off; a null `live` means no operating state is reported, as under maintenance.
     */
    operating_time: { seconds: number; live: boolean | null; sinceMs?: number } | null;
    last_intervention: LastIntervention | null;
}

// ---- History ----

export type AssetHistoryEventType =
    | 'ALARM'
    | 'DIAGNOSTIC'
    | 'INTERVENTION'
    | 'INSPECTION'
    | 'CONFIRMED'
    | 'MESSAGE'
    | 'REPLY'
    | 'TECH_REPORT'
    | 'OFFLINE_REPORT'
    | 'RESULTS_REPORT';

export interface HistoryAttachment {
    name: string;
    size: string;
    pages?: string;
}

export interface HistoryAlarmDetail {
    from: number;
    to: number;
    delta: number;
    variables: string[]; // e.g. "Vibración axial 8.2 mm/s umbral 5.5"
    tag: string; // e.g. "ALARMA · 27/05/2026 · 12:01"
}

export interface HistoryExpanded {
    sender?: { name: string; email: string };
    linkedBadge?: string;
    aiSummary?: string;
    attachment?: HistoryAttachment;
    alarm?: HistoryAlarmDetail;
    footer?: string;
}

export interface AssetHistoryEvent {
    id: number;
    ts: string; // ISO
    group: string; // pre-computed relative group header (e.g. "Today", "This week")
    type: AssetHistoryEventType;
    badge: string;
    title: string;
    subtitle?: string;
    expanded?: HistoryExpanded;
}

export interface AssetHistoryResponse {
    items: AssetHistoryEvent[];
}

// ============================================================================
// UI state
// ============================================================================

export type ViewMode = 'layout' | 'cards';
export type AssetSortKey = 'health' | 'name';
export type SortDir = 'asc' | 'desc';
